import LoadingButton from '@mui/lab/LoadingButton';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { DateTimePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ControlledCheckbox from '~components/Form/ControlledCheckbox';
import ControlledNumberField from '~components/Form/ControlledNumberField';
import OberonDialog from '~components/OberonDialog';
import { CreateCampaignLeadList } from '~pages/CampaignManagement/CampaignDetails/CampaignLeadLists/useCampaignListSearch/domain';
import { ListPriority } from '~pages/CampaignManagement/domain';

interface CreateLeadListModalProps {
  open: boolean;
  submitting: boolean;
  onAccept: (data: CreateCampaignLeadList) => Promise<void>;
  onClose: () => void;
}

interface Form {
  name: string;
  description: string;
  priority: number;
  setStartEndTime: boolean;
  startTime: string | null;
  endTime: string | null;
}

const CreateLeadListModal = ({ open, submitting, onAccept, onClose }: CreateLeadListModalProps) => {
  const [endTimeValidationError, setEndTimeValidationError] = useState<string | undefined>(undefined);
  const {
    control,
    watch,
    reset,
    handleSubmit,
    formState: { errors },
  } = useForm<Form>({
    defaultValues: {
      name: '',
      description: '',
      priority: 1,
      setStartEndTime: false,
      startTime: null,
      endTime: null,
    },
    mode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
  });
  const [setStartEndTime, startTime] = watch(['setStartEndTime', 'startTime']);
  const fieldsDateToMinDate = useMemo(() => (startTime ? DateTime.fromISO(startTime) : undefined), [startTime]);

  // Manages optional start and end time properties within schema
  useEffect(() => {
    if (!setStartEndTime) {
      setEndTimeValidationError(undefined);
    }
  }, [setStartEndTime]);

  const onSubmit = handleSubmit(async (formData: Form) => {
    try {
      await onAccept({
        name: formData.name,
        description: formData.description,
        priority: formData.priority,
        startTime: formData.startTime || undefined,
        endTime: formData.endTime || undefined,
      });
      reset();
    } catch (e) {
      // Do nothing, catch error to prevent form reset on a failed action
    }
  });

  return (
    <OberonDialog
      open={open}
      onSubmit={onSubmit}
      onClose={onClose}
      title='Create List'
      content={
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Controller
              name='name'
              control={control}
              rules={{ required: 'List name is required.' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  variant='outlined'
                  id='name'
                  label='List Name'
                  disabled={submitting}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <Controller
              name='description'
              control={control}
              rules={{ required: 'List description is required.' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  multiline
                  rows={4}
                  variant='outlined'
                  id='description'
                  label='List Description'
                  disabled={submitting}
                  error={Boolean(errors.description)}
                  helperText={errors.description?.message}
                />
              )}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledNumberField
              name='priority'
              control={control}
              rules={{
                required: 'List priority is required.',
                validate: (value) => {
                  if (value < ListPriority.Min || value > ListPriority.Max) {
                    return `List priority must be between ${1} - ${20}`;
                  }
                  return true;
                },
              }}
              label='List Priority'
              disabled={submitting}
              required={true}
              error={Boolean(errors.priority)}
              helperText={errors.priority?.message}
            />
          </Grid>

          <Grid item xs={12}>
            <ControlledCheckbox
              name='setStartEndTime'
              label='Set Start or End Time?'
              control={control}
              disabled={submitting}
            />
          </Grid>

          {setStartEndTime && (
            <>
              <Grid item xs={12} md={6}>
                <Controller
                  name='startTime'
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      disableMaskedInput
                      componentsProps={{
                        actionBar: {
                          actions: ['clear'],
                        },
                      }}
                      disabled={submitting}
                      label='Start Time'
                      inputFormat='dd/MM/yyyy hh:mm a'
                      renderInput={(params) => <TextField {...params} fullWidth variant='outlined' />}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Controller
                  name='endTime'
                  control={control}
                  render={({ field }) => (
                    <DateTimePicker
                      {...field}
                      disableMaskedInput
                      componentsProps={{
                        actionBar: {
                          actions: ['clear'],
                        },
                      }}
                      minDate={fieldsDateToMinDate}
                      onError={(reason) => {
                        if (reason === 'minDate') {
                          setEndTimeValidationError('List End Time should not be before Start Time.');
                          return;
                        }

                        setEndTimeValidationError(undefined);
                      }}
                      disabled={submitting}
                      label='End Time'
                      inputFormat='dd/MM/yyyy hh:mm a'
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          fullWidth
                          variant='outlined'
                          error={Boolean(endTimeValidationError)}
                          helperText={endTimeValidationError}
                        />
                      )}
                    />
                  )}
                />
              </Grid>
            </>
          )}
        </Grid>
      }
      actionFooter={
        <>
          <Button variant='text' disabled={submitting} onClick={onClose}>
            Close
          </Button>

          <LoadingButton
            type='submit'
            variant='contained'
            disableElevation
            color='primary'
            disabled={submitting}
            loading={submitting}>
            Create
          </LoadingButton>
        </>
      }
    />
  );
};

export default CreateLeadListModal;
